import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import TimeIcon from "@mui/icons-material/AccessTime";
import { AuthContext } from "./AuthContext";

const PatchingReminderContext = createContext(null);

export const usePatchingReminder = () => useContext(PatchingReminderContext);

const PATCHING_CHECK_INTERVAL_MS = 60 * 60 * 1000;

const getTodayDateString = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(today.getDate()).padStart(2, "0")}`;
};

const getStorageKey = () =>
  `patching_reminder_dismissed_${getTodayDateString()}`;

/** API schedule row (+ template) → shape used by the reminder dialog */
const apiScheduleToReminderUpdate = (row) => {
  if (!row) return null;
  const tpl = row.template || {};
  let description = tpl.body != null ? String(tpl.body) : "";
  if (row.parameters && typeof row.parameters === "object") {
    Object.entries(row.parameters).forEach(([k, v]) => {
      description = description.split(`{{${k}}}`).join(String(v));
    });
  }
  return {
    startTime: row.scheduled_start_time,
    endTime: row.scheduled_end_time,
    description,
    status: row.status,
    activeUntil: row.scheduled_end_time || row.scheduled_start_time,
  };
};

const isDismissedToday = () => {
  const dismissed = localStorage.getItem(getStorageKey());
  return dismissed === "true";
};

const dismissForToday = () => {
  localStorage.setItem(getStorageKey(), "true");
};

const formatTime = (timeString) => {
  if (!timeString) return "";

  const date = new Date(timeString);

  // If the value is a valid Date (ISO or datetime-local)
  if (!isNaN(date.getTime())) {
    const formattedDate = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${formattedDate} ${formattedTime}`;
  }

  // Fallback for plain time strings (e.g., "14:05", "14:05", etc.)
  const parts = timeString.split(":");
  if (parts.length >= 2) {
    const [h, m] = parts;
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return `${formattedDate} ${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  }

  // Last fallback
  return timeString;
};

const isToday = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
};

const isActive = (patchingUpdate) => {
  if (!patchingUpdate) return false;

  // Check if status is completed or cancelled
  if (
    patchingUpdate.status === "completed" ||
    patchingUpdate.status === "cancelled"
  ) {
    return false;
  }

  // Check if active_until is in the future
  if (patchingUpdate.activeUntil) {
    const activeUntil = new Date(patchingUpdate.activeUntil);
    if (Number.isNaN(activeUntil.getTime())) return false;
    return activeUntil > new Date();
  }

  return true;
};

export function PatchingReminderProvider({ children }) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [patchingUpdate, setPatchingUpdate] = useState(null);
  const authContext = useContext(AuthContext);
  const { global_authState, getLatestPatchingUpdate } = authContext || {};
  const intervalRef = useRef(null);
  const isDialogOpenRef = useRef(false);
  const inFlightRef = useRef(false);
  const lastCheckedAtRef = useRef(0);

  // Keep ref in sync with state
  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  const checkPatchingUpdate = useCallback(async () => {
    if (!getLatestPatchingUpdate || !global_authState?.uid) {
      return;
    }

    const now = Date.now();
    if (
      inFlightRef.current ||
      now - lastCheckedAtRef.current < PATCHING_CHECK_INTERVAL_MS
    ) {
      return;
    }

    inFlightRef.current = true;
    lastCheckedAtRef.current = now;

    try {
      const response = await getLatestPatchingUpdate();
      const formattedUpdate = apiScheduleToReminderUpdate(
        response?.data ?? null
      );
      if (formattedUpdate) {
        setPatchingUpdate(formattedUpdate);

        // Check if patching is today and active
        const isPatchingToday = isToday(formattedUpdate.startTime);
        const isPatchingActive = isActive(formattedUpdate);

        if (
          isPatchingToday &&
          isPatchingActive &&
          !isDismissedToday() &&
          !isDialogOpenRef.current
        ) {
          setIsDialogOpen(true);
        } else if (!isPatchingToday || !isPatchingActive) {
          // Close dialog if patching is no longer today or active
          if (isDialogOpenRef.current) {
            setIsDialogOpen(false);
          }
        }
      } else {
        // No patching update found
        setPatchingUpdate(null);
        if (isDialogOpenRef.current) {
          setIsDialogOpen(false);
        }
      }
    } catch (error) {
      // Silently fail - don't show errors for background checks
      console.error("Failed to check patching update:", error);
    } finally {
      inFlightRef.current = false;
    }
  }, [getLatestPatchingUpdate, global_authState?.uid]);

  useEffect(() => {
    const isLoggedIn = global_authState?.uid;

    if (!isLoggedIn || !getLatestPatchingUpdate) {
      return;
    }

    checkPatchingUpdate();

    intervalRef.current = setInterval(() => {
      checkPatchingUpdate();
    }, PATCHING_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [checkPatchingUpdate, getLatestPatchingUpdate, global_authState?.uid]);

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleDismissForToday = () => {
    setIsDialogOpen(false);
    dismissForToday();
  };

  return (
    <PatchingReminderContext.Provider value={null}>
      {children}
      <PatchingReminderDialog
        open={isDialogOpen}
        onClose={handleClose}
        onDismissForToday={handleDismissForToday}
        patchingUpdate={patchingUpdate}
      />
    </PatchingReminderContext.Provider>
  );
}

function PatchingReminderDialog({
  open,
  onClose,
  onDismissForToday,
  patchingUpdate,
}) {
  const theme = useTheme();
  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClose();
    }
  };

  const startTime = patchingUpdate?.startTime || "";
  const endTime = patchingUpdate?.endTime || "";
  const description = patchingUpdate?.description || "";

  const timeRange =
    startTime && endTime
      ? `${formatTime(startTime)} - ${formatTime(endTime)}`
      : startTime
        ? `Starting at ${formatTime(startTime)}`
        : "Today";

  const w = theme.palette.warning;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          backdropFilter: "blur(12px)",
          boxShadow: (t) =>
            `0 8px 32px ${alpha(t.palette.warning.dark, 0.18)}, 0 2px 12px rgba(0, 0, 0, 0.06)`,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.warning.main, 0.28),
          position: "relative",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            position: "relative",
            px: { xs: 2.5, sm: 3 },
            py: { xs: 2.5, sm: 3 },
            background: (t) => alpha(t.palette.warning.main, 0.12),
            borderBottom: "1px solid",
            borderColor: (t) => alpha(t.palette.warning.main, 0.22),
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.75}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: `linear-gradient(135deg, ${w.dark} 0%, ${w.main} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  boxShadow: `0 4px 14px ${alpha(w.dark, 0.35)}`,
                  color: (t) => t.palette.warning.contrastText,
                }}
              >
                <WarningIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.25,
                    background: `linear-gradient(45deg, ${w.dark} 20%, ${w.main} 85%)`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontSize: { xs: "1.15rem", sm: "1.25rem" },
                  }}
                >
                  System Maintenance
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    mt: 0.35,
                    color: (t) => alpha(t.palette.warning.dark, 0.85),
                    fontSize: "0.8125rem",
                  }}
                >
                  Important notification regarding system availability
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={onClose}
              size='small'
              sx={{
                color: (t) => alpha(t.palette.warning.dark, 0.75),
                bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                "&:hover": {
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.22),
                  color: "warning.dark",
                },
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          bgcolor: (t) => alpha(t.palette.warning.main, 0.03),
        }}
      >
        <br />
        <Stack spacing={2.5}>
          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              background: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(10px)",
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.warning.main, 0.2),
              boxShadow: (t) =>
                `0 2px 12px ${alpha(t.palette.warning.main, 0.08)}`,
            }}
          >
            <Stack direction='row' spacing={1.5} alignItems='flex-start'>
              <Box
                sx={{
                  mt: 0.25,
                  p: 0.75,
                  borderRadius: 1.25,
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                  color: "warning.dark",
                  display: "flex",
                }}
              >
                <TimeIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  sx={{ color: "warning.dark", mb: 0.5 }}
                >
                  Scheduled downtime
                </Typography>
                <Typography variant='body2' sx={{ lineHeight: 1.65 }}>
                  {startTime ? (
                    <Typography
                      component='span'
                      fontWeight={600}
                      sx={{ color: "warning.dark" }}
                    >
                      {timeRange}
                    </Typography>
                  ) : (
                    <Typography
                      component='span'
                      sx={{ color: (t) => alpha(t.palette.warning.dark, 0.75) }}
                    >
                      Time to be announced
                    </Typography>
                  )}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ px: 0.25 }}>
            {description ? (
              <Typography
                variant='body1'
                sx={{
                  color: (t) => alpha(t.palette.warning.dark, 0.92),
                  lineHeight: 1.75,
                  whiteSpace: "pre-wrap",
                }}
              >
                {description}
              </Typography>
            ) : (
              <Typography
                variant='body1'
                sx={{
                  color: (t) => alpha(t.palette.warning.dark, 0.72),
                  lineHeight: 1.75,
                }}
              >
                Our engineering team will be performing scheduled maintenance to
                improve system performance. During this time, you may experience
                intermittent connectivity issues.
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2.5,
          flexDirection: "column",
          alignItems: "stretch",
          background: (t) => alpha(t.palette.warning.main, 0.12),
          borderTop: "1px solid",
          borderColor: (t) => alpha(t.palette.warning.main, 0.18),
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            onClick={onClose}
            variant='text'
            sx={{
              minWidth: 0,
              px: 2,
              color: (t) => alpha(t.palette.warning.dark, 0.8),
              fontWeight: 600,
              py: 1.1,
              borderRadius: 2,
              mr: 0,
              "&:hover": {
                bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                color: "warning.dark",
              },
              alignSelf: "center",
              transition: "background-color 0.2s",
              width: "auto",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                display: "inline-block",
                px: 0.5,
                borderRadius: 1,
                transition: "inherit",
                "&:hover": {
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                },
              }}
            >
              Remind me later
            </Box>
          </Button>

          <Button
            onClick={onDismissForToday}
            variant='contained'
            disableElevation
            sx={{
              minWidth: 0,
              px: 3,
              width: "160px",
              py: 1.2,
              borderRadius: 2,
              fontWeight: 700,
              bgcolor: "warning.main",
              color: (t) => t.palette.warning.contrastText,
              boxShadow: (t) =>
                `0 4px 16px ${alpha(t.palette.warning.dark, 0.35)}`,
              "&:hover": {
                bgcolor: "warning.dark",
                boxShadow: (t) =>
                  `0 6px 20px ${alpha(t.palette.warning.dark, 0.4)}`,
              },
              alignSelf: "center",
              transition: "background-color 0.2s, box-shadow 0.2s",
            }}
          >
            Understood
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
